import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import {
  LeagueEditType,
  leagueEditSchema,
} from "@/pages/leagues/schemas/league.edit.form";
import { ReadOnlyNote } from "@/pages/sports/components/read-only-note";
import { useSportsRepository } from "@/pages/sports/hooks/use-sports-repository";

/** Curator edit for a league: name, enabled, tier, status, hide, media. The rest is TINA's. */
export const LeagueEdit = () => {
  const { getLeague, updateLeague } = useSportsRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<LeagueEditType>();
  const [source, setSource] = React.useState<Record<string, any>>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  const schemaProvider = useMemo(() => new ZodProvider(leagueEditSchema), []);

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    getLeague(id.toString())
      .then((response) => {
        // getOne resolves the flat LeagueDbDto, as in PlaysEdit.
        const raw = response as Record<string, any>;
        const data = parseRead(
          leagueEditSchema,
          raw,
          "league",
        ) as LeagueEditType;
        setSource(raw);
        setValues(data);
        setTitle("Edit " + data.name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get league",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<LeagueEditType, any, LeagueEditType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: LeagueEditType,
    form: UseFormReturn<LeagueEditType, any, LeagueEditType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    setWarnWhen(false);
    await updateLeague({ ...dirty, id: id?.toString() });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {source && (
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Highlightly ID: </span>
              <span className="font-mono">
                {source.metadata?.sourceId ?? "—"}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Current season: </span>
              <span>{source.currentSeason ?? "—"}</span>
            </div>
            <ReadOnlyNote />
          </div>
        )}
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
